import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useGetTemplatesByTenant } from '@/api/templates/useGetTemplatesByTenant';
import { Button } from '@/component-library/primitives/Button/Button';
import Loader from '@/component-library/primitives/Loader/Loader';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import SearchField from '@/component-library/widgets/SearchField/SearchField';
import Table from '@/component-library/widgets/Table/Table';
import { HttpMethodsEnum, PermissionsEnum } from '@/modules/shared/enums/enums';
import { usePermissionChecker } from '@/modules/shared/hooks/usePermissionChecker';
import { AppRoutes } from '@/utils/routes/router';

import { useTemplatesTable } from './useTemplatesTable';

const Templates = () => {
  const { data, isLoading } = useGetTemplatesByTenant();
  const { columns, deleteConfirmationModal } = useTemplatesTable();

  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });
  const isActionAllowed = usePermissionChecker(PermissionsEnum.TEMPLATES, HttpMethodsEnum.POST);
  const [filterValue, setFilterValue] = useState('');

  const filteredData = useMemo(() => {
    return data?.filter((row) =>
      Object.values(row).some(
        (value) => value !== null && value.toString().toLowerCase().includes(filterValue.toLowerCase()),
      ),
    );
  }, [data, filterValue]);

  const handleNavigate = (route: string, isActionAllowed: boolean) => {
    if (!isActionAllowed) {
      setToastInfo({ message: t('CommonErrors.actionNotAllowed'), toastType: 'error' });
      setOpen(true);
      return;
    }
    navigate(route);
  };

  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row items-center mb-4">
        <div className="flex-grow justify-start">
          <SearchField initialValue={filterValue} setSearchValue={setFilterValue} />
        </div>
        {filteredData && filteredData.length > 0 && (
          <div className="justify-end">
            <Button className="!w-[10.5rem]" onClick={() => handleNavigate(AppRoutes.createTemplate, isActionAllowed)}>
              {t('TemplatesPage.buttonNewTemplate')}
            </Button>
          </div>
        )}
      </div>
      {deleteConfirmationModal}
      {isLoading ? (
        <Loader />
      ) : filteredData && filteredData.length > 0 ? (
        <Table columns={columns} data={filteredData ?? []} />
      ) : (
        <NoDataBoard
          description={t('NoDataBoard.addYourFirstTemplate')}
          actionButton={
            <Button
              className="!w-[10.5rem] mt-4"
              onClick={() => handleNavigate(AppRoutes.createTemplate, isActionAllowed)}
            >
              {t('TemplatesPage.buttonNewTemplate')}
            </Button>
          }
        />
      )}
      <ProvideToasts>
        <Toast
          isOpen={open}
          setOpen={setOpen}
          message={toastInfo.message}
          toastType={toastInfo.toastType as ToastType}
        />
      </ProvideToasts>
    </div>
  );
};

export default Templates;
